import React, {RefObject, useEffect, useState} from "react";
import Grid from "@material-ui/core/Grid";
import {Button, CircularProgress, Typography} from "@material-ui/core";
import {AudioFileModel} from "../../shared/models/AudioTestModel";

const fullyBufferedTolerance = 0.05;

export interface AudioLoadingProgress {
  completed: number;
  total: number;
  percentage: number;
}

export function getAudioBufferedFraction(audio: HTMLAudioElement): number {
  if (!Number.isFinite(audio.duration) || audio.duration <= 0) return 0;
  if (audio.networkState === HTMLMediaElement.NETWORK_IDLE &&
    audio.readyState >= HTMLMediaElement.HAVE_ENOUGH_DATA) return 1;

  let bufferedEnd = 0;
  for (let index = 0; index < audio.buffered.length; index += 1) {
    const start = audio.buffered.start(index);
    if (start > bufferedEnd + fullyBufferedTolerance) break;
    bufferedEnd = Math.max(bufferedEnd, audio.buffered.end(index));
  }

  return Math.min(1, bufferedEnd / audio.duration);
}

/**
 * `canplaythrough` only predicts sequential playback from the current time.
 * Clip switching seeks to an arbitrary time, so require continuous buffered
 * media from the beginning through the full duration instead.
 */
export function isAudioFullyBuffered(audio: HTMLAudioElement): boolean {
  if (!Number.isFinite(audio.duration) || audio.duration <= 0) return false;

  // Some browsers finish the HTTP transfer but report the final media frame
  // just short of `duration` in `buffered`. In that case, the completed
  // network state plus HAVE_ENOUGH_DATA is the strongest available signal.
  if (audio.networkState === HTMLMediaElement.NETWORK_IDLE &&
    audio.readyState >= HTMLMediaElement.HAVE_ENOUGH_DATA) return true;

  return getAudioBufferedFraction(audio) >= 1 - fullyBufferedTolerance / audio.duration;
}

/**
 * Download the complete media response ourselves before assigning it to an
 * audio element. `preload="auto"` is only a browser hint: Chrome and Safari
 * may retain just enough remote ranges to start playback, which is not enough
 * for a synchronized switch to an arbitrary timestamp.
 */
async function downloadAudioSource(src: string): Promise<string> {
  const response = await fetch(src);
  // A normal fetch without a Range header must yield the complete resource.
  // Treat a partial response as a failure rather than accidentally turning a
  // truncated Blob into an apparently-ready clip.
  if (!response.ok || response.headers.has('content-range'))
    throw new Error(`Unable to fully download audio (${response.status})`);

  const blob = await response.blob();
  if (!blob.size) throw new Error('Downloaded audio is empty');
  return URL.createObjectURL(blob);
}

function releaseAudioElements(refs: RefObject<HTMLAudioElement>[]) {
  refs.forEach((ref) => {
    const audio = ref.current;
    if (!audio) return;
    audio.pause();
    audio.removeAttribute('src');
    audio.load();
  });
}

export function useAllAudioRefsReady(refs: RefObject<HTMLAudioElement>[], audios: AudioFileModel[], enabled = true) {
  const [loading, setLoading] = useState(enabled);
  const [loadError, setLoadError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [progress, setProgress] = useState<AudioLoadingProgress>({completed: 0, total: refs.length, percentage: 0});
  // Key Blob URLs by their original media URL rather than their position in a
  // list. The displayed list can be randomized after this hook starts
  // downloading, so positional matching would play the wrong clip.
  const [sources, setSources] = useState<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;
    let objectUrls: string[] = [];

    if (!enabled) {
      releaseAudioElements(refs);
      setSources({});
      setLoading(false);
      setLoadError(false);
      setProgress({completed: 0, total: refs.length, percentage: 0});
      return () => {};
    }

    setLoading(true);
    setLoadError(false);
    setSources({});
    releaseAudioElements(refs);
    setProgress({completed: 0, total: audios.length, percentage: 0});

    // Full response bodies are held in Blob URLs. Once this resolves, no
    // later seek can create a request to the remote audio server.
    Promise.all(audios.map(async (audio, index) => {
      try {
        const objectUrl = await downloadAudioSource(audio.src);
        objectUrls[index] = objectUrl;
        if (!cancelled) {
          const completed = objectUrls.filter(Boolean).length;
          setProgress({
            completed,
            total: audios.length,
            percentage: audios.length ? Math.round(completed / audios.length * 100) : 100,
          });
        }
        return true;
      } catch (_) {
        return false;
      }
    })).then((results) => {
      if (cancelled) {
        objectUrls.forEach((url) => URL.revokeObjectURL(url));
        return;
      }
      if (results.some((downloaded) => !downloaded)) {
        objectUrls.forEach((url) => URL.revokeObjectURL(url));
        setLoadError(true);
        setLoading(false);
        return;
      }
      setSources(audios.reduce<Record<string, string>>((sourceMap, audio, index) => {
        sourceMap[audio.src] = objectUrls[index];
        return sourceMap;
      }, {}));
      setLoading(false);
    });

    return () => {
      cancelled = true;
      releaseAudioElements(refs);
      objectUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [enabled, attempt]);

  return {loading, loadError, progress, sources, retry: () => setAttempt((value) => value + 1)};
}

export function AudioLoading({showing, error, onRetry, progress, onLoadingChange}: {
  showing: boolean,
  error?: boolean,
  onRetry?: () => void,
  progress?: AudioLoadingProgress,
  onLoadingChange?: (loading: boolean) => void,
}) {
  useEffect(() => onLoadingChange?.(showing), [showing, onLoadingChange]);

  if (!showing && !error) return null;

  if (error) return <Grid container spacing={2} alignItems="center">
    <Grid item xs={12}><Typography color="error">Some audio clips could not be loaded.</Typography></Grid>
    {onRetry && <Grid item><Button variant="outlined" color="primary" onClick={onRetry}>Retry loading clips</Button></Grid>}
  </Grid>;

  return <Grid container spacing={2} alignItems="center">
    <Grid item><CircularProgress color="secondary"/></Grid>
    <Grid item>
      <Typography>Clips are downloading before playback, just a moment...</Typography>
      {progress && <Typography variant="body2" color="textSecondary">
        {progress.completed} of {progress.total} clips ready ({progress.percentage}%)
      </Typography>}
    </Grid>
  </Grid>
}
